import React, { useEffect, useState, Fragment } from 'react';

// Material
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';

// Components
import Dropdown from '../../../../components/inputs/dropdown';
import Textfield from '../../../../components/inputs/input';

// Hooks
import useAditionals from '../../../../hooks/orders/useAditionals';
import useNotify from '../../../../hooks/notify';
import PromotionsAutocomplete from '../../../../components/additionalPromotionsAutocomplete';

import { PromotionTypes, additionalsWithoutValue } from '../../../../models/PromotionTypes'

const ModalAdditionalPromotions = ({ additionalPromotions, onChange, onClose, open }) => {
    const { aditionals } = useAditionals();
    const { Notify, variants } = useNotify();
    const [promotionType, setPromotionType] = useState("");
    const [promotions, setPromotions] = useState([]);
    const [value, setValue] = useState("");
    const [obs, setObs] = useState("");
    const [promotionsTypeSelected, setPromotionsTypeSelected] = useState([]);
    const [promotionSelected, setPromotionSelected] = useState();

    useEffect(() => {
        if (aditionals.length > 0) {
            const promotion = aditionals[0];
            setPromotionType(promotion.type);
        }
    }, [aditionals])

    useEffect(() => {
        if (promotionType) {
            const type = aditionals
                .find(p => p.type === promotionType);

            setPromotions(type?.promotions || [])
            setValue("");
        }
    }, [aditionals, promotionType]);

    useEffect(() => {
        if (promotions && promotions.length > 0)
            setPromotionSelected(promotions[0])
        else
            setPromotionSelected(null)
    }, [promotions])

    useEffect(() => {
        if (additionalPromotions)
            setPromotionsTypeSelected(additionalPromotions);
    }, [additionalPromotions])

    const handleAddPromotion = () => {
        if (!promotionSelected?.id) return;

        if (promotionType === PromotionTypes.ManualDiscountPercentage ||
            promotionType === PromotionTypes.ManualDiscountFixed) {
            if (isNaN(value)) {
                Notify("El formato del valor ingresado es invalido, tiene que ser numerico", variants.ERROR);
                return;
            }
        }

        const promotionToAdd = promotionsTypeSelected
            .filter(p => p.id !== promotionSelected?.id);

        const promotion = promotions
            .find(p => p.id === promotionSelected?.id);

        if (!promotion) return;

        promotionToAdd.push({
            id: promotion.id,
            title: promotion.name,
            type: promotion.type,
            obs,
            value
        })

        setPromotionsTypeSelected(promotionToAdd);
        setValue("");
        setObs("");
    }

    const handleRemovePromotion = (promotionId) => {
        const promotionToAdd = promotionsTypeSelected
            .filter(p => p.id !== promotionId);

        setPromotionsTypeSelected(promotionToAdd);
    }

    const handleSave = () => {
        if (onChange) onChange(promotionsTypeSelected);
        if (onClose) onClose();
    }

    const handlePromotionChange = (event, value) => {
        setPromotionSelected(value || null)
    }
    const handlePromoAdditionalCode = (event) => {
        const code = event.target.value
            .toLocaleUpperCase()
            .replace(/\s/g, '')
            .replace(/,/g, '.')
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "");

        setValue(code);
    }
    const handlePromoObs = (event) => {
        setObs(event.target.value);
    }
    const promotionNeedsValue = () =>
        !additionalsWithoutValue.includes(promotionType);

    const getSize = (field) => {
        const type = promotionSelected?.type;

        switch (type) {
            case PromotionTypes.Fidelity:
                return { Obs: 8, Value: 0, Add: 4 }[field] ?? 1;

            case PromotionTypes.ManualDiscountPercentage:
            case PromotionTypes.ManualDiscountFixed:
                return { Obs: 4, Value: 4, Add: 4 }[field] ?? 1;

            default:
                return { Obs: 0, Value: 8, Add: 4 }[field] ?? 1;
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle id="alert-dialog-title">
                Promociones Adicionales
            </DialogTitle>
            <DialogContent style={{ minWidth: 430 }}>
                <DialogContentText id="alert-dialog-description">
                    Desde este panel se podran visualizar las promociones adicionales, como tambien se podran
                    agregar nuevas o eliminar las que no se requieran.
                </DialogContentText>
                <Grid container spacing={2} alignItems='center'>
                    <Grid size={6}>
                        <Dropdown
                            fullWidth
                            size='small'
                            name='promotionsTypes'
                            placeholder="Tipo de promocion"
                            onChange={(option) => setPromotionType(option.target.value)}
                            disabled={!aditionals || aditionals.length === 0}
                            getOptionLabel={(option) => option?.title}
                            getOptionValue={(option) => option?.type}
                            value={promotionType}
                            values={aditionals || []}
                        />
                    </Grid>
                    <Grid size={6}>
                        <PromotionsAutocomplete
                            onChange={handlePromotionChange}
                            value={promotionSelected}
                            promotions={promotions}

                        />
                    </Grid>

                    {
                        promotionNeedsValue() &&
                        <Grid size={getSize("Value")}>
                            <Textfield
                                fullWidth
                                size='small'
                                name="value"
                                label="Ingrese el valor"
                                onChange={handlePromoAdditionalCode}
                                disabled={!promotions || promotions.length === 0 || !promotionNeedsValue()}
                                value={value}
                            />
                        </Grid>
                    }
                    {
                        (promotionSelected?.type === PromotionTypes.Fidelity ||
                            promotionSelected?.type === PromotionTypes.ManualDiscountPercentage ||
                            promotionSelected?.type === PromotionTypes.ManualDiscountFixed) &&
                        <Grid size={getSize("Obs")}>
                            <Textfield
                                fullWidth
                                size='small'
                                name="obs"
                                label="Observación"
                                onChange={handlePromoObs}
                                disabled={false}
                                value={obs}
                            />
                        </Grid>
                    }
                    <Grid size={promotionNeedsValue() ? getSize("Add") : getSize("Add")}>
                        <Button
                            fullWidth
                            color='primary'
                            variant='contained'
                            onClick={handleAddPromotion}
                            disabled={!promotionSelected || (!value && promotionNeedsValue())}
                        >
                            Agregar
                        </Button>
                    </Grid>
                    <Grid size={12}>
                        <Paper variant='outlined' style={{ padding: 8, marginBottom: 8 }}>
                            <Grid container direction='column' spacing={1}>
                                {
                                    !!promotionsTypeSelected.length &&
                                    <>
                                        <Grid container>
                                            <Grid size={4}>
                                                <Typography>Titulo</Typography>
                                            </Grid>
                                            <Grid size={3}>
                                                <Typography>Valor</Typography>
                                            </Grid>
                                            <Grid size={3}>
                                                <Typography>Observación</Typography>
                                            </Grid>
                                            <Grid size={2}>
                                                <Typography align='right'>Acciones</Typography>
                                            </Grid>
                                        </Grid>
                                        {
                                            promotionsTypeSelected.map((promo, index) =>
                                                <Grid key={index} container justifyContent='space-between'>
                                                    <Grid wrap='nowrap' size={5}>
                                                        <Typography component='span' variant='body1'>
                                                            <strong>{promo.title}</strong>
                                                        </Typography>
                                                        <Typography variant='caption' display='block'>
                                                            {promo.description}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid wrap='nowrap' size={2}>
                                                        <Typography>
                                                            <strong>{promo.value}</strong>
                                                        </Typography>
                                                    </Grid>
                                                    <Grid wrap='nowrap' size={3}>
                                                        <Typography>
                                                            <strong>{promo.obs}</strong>
                                                        </Typography>
                                                    </Grid>
                                                    <Grid wrap='nowrap' container justifyContent='flex-end' size={2}>
                                                        <IconButton
                                                            color='primary'
                                                            size='small'
                                                            onClick={() => handleRemovePromotion(promo.id)}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Grid>
                                                </Grid>
                                            )
                                        }
                                    </>
                                }
                                {
                                    !promotionsTypeSelected.length &&
                                    <Grid>
                                        <Typography component='span' variant='body1'>
                                            No hay promociones aplicadas
                                        </Typography>
                                    </Grid>
                                }
                            </Grid>
                        </Paper>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onClose}
                    color="primary"
                >
                    Cerrar
                </Button>
                <Button
                    onClick={handleSave}
                    color="primary"
                    autoFocus
                >
                    Guardar
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ModalAdditionalPromotions;